/**
 * Palavras de ativação (spec 011). Puro: recebe o trecho que o reconhecedor
 * LOCAL do Chrome transcreveu e diz qual nome apareceu, com qual grafia e
 * com qual pedido colado.
 *
 * Dois nomes, cada um com um dono (pedido do Leopoldo, 03/10/2026 — com um
 * nome só, o Chrome cortava "Jarvis… manda entrar" na pausa e o "Jarvis"
 * sozinho ligava o ao vivo):
 *   - "Jarvis" é o DITADO: sozinho (ou com qualquer fala) abre o ditado no ao
 *     vivo (decisão de 25/09/2026 — sem "ok", sem janela de cancelar);
 *     "Jarvis, cancela" não abre; "Jarvis, desliga" desliga. O "ok" antes,
 *     se vier, só vai pro diário (`comOk`).
 *   - "Atlas" é o SISTEMA (ver `commands.ts`) e nunca abre o ditado:
 *       "Atlas, abre o próximo"                     → ficha do próximo paciente
 *       "Atlas, manda entrar" / "chama o próximo"   → "Mandar entrar" pra recepção
 *       "Atlas, abre a agenda" / "volta pra agenda" → agenda
 *       "Atlas, imprime tudo / os óculos / …"       → impressão (só card completo)
 *       "Atlas, salve" / "registra"                 → "Registrar no prontuário"
 *       "Atlas, pause"                              → pausa o ao vivo
 *       "Atlas, continua" / "retoma"                → volta a ouvir (pausado)
 *       "Atlas, pare" / "desliga" / "para o ditado" → desliga o ao vivo
 *     "Atlas" sozinho espera o pedido na frase seguinte (`matchSystemCommand`).
 *
 * `QUASE` existe pra calibrar: se o reconhecedor escrever "Jarbas" na voz do
 * médico, o diário mostra, e a grafia entra (ou a frase muda) com número na mão.
 */

import type { PrintTarget } from '../live';

/** Quem foi chamado: o ditado ou o sistema. */
export type WakeNome = 'jarvis' | 'atlas';

/** Pedidos ao sistema — só depois de "Atlas" (ou na transcrição do ao vivo). */
export type SystemCommand =
  | 'proximo'
  | 'mandarEntrar'
  | 'agenda'
  | 'imprimir'
  | 'salvar'
  | 'pausar'
  | 'retomar'
  | 'parar';

/** O que vem colado no "Jarvis" (o ditado). */
export type DitadoCommand = 'aoVivo' | 'abrir' | 'cancela' | 'parar';

export type WakeCommand = DitadoCommand | SystemCommand;

interface WakeMatchBase {
  /** O nome veio precedido de "ok" (em qualquer grafia). */
  comOk: boolean;
  /** Grafia do nome como o reconhecedor escreveu (normalizada). */
  variante: string;
  /** Grafia parecida mas NÃO aceita ("jarbas"): só pro diário de calibração. */
  quase: boolean;
}

/** "jarvis" (ditado) ou "atlas" (sistema), com o comando colado logo depois, se houver. */
export type WakeMatch =
  | (WakeMatchBase & { nome: 'jarvis'; comando: DitadoCommand | null; alvos?: undefined })
  | (WakeMatchBase & {
      nome: 'atlas';
      comando: SystemCommand | null;
      /** O que imprimir, quando o comando é "imprimir". */
      alvos?: PrintTarget[];
    });

export interface SystemCommandMatch {
  comando: SystemCommand;
  alvos?: PrintTarget[];
}

/** Minúsculas, sem acento, sem pontuação, espaços simples. */
export function normalizeSpeech(texto: string): string {
  return texto
    .toLowerCase()
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .replace(/[^a-z0-9\s]/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
}

const GRAFIAS = 'jarvis|jarviz|jarves|djarvis|jervis';
const GRAFIAS_ATLAS = 'atlas';
const NOME = new RegExp(`\\b(${GRAFIAS}|${GRAFIAS_ATLAS})\\b`);
const E_ATLAS = new RegExp(`^(?:${GRAFIAS_ATLAS})$`);
const QUASE = /\b(jarbas|jarbis|garvis|jarvas)\b/;
/** "ok" do jeito que o reconhecedor escreve, colado antes do nome. */
const OK_ANTES = /(?:^|\s)(ok|okay|okey|oke|okei|oquei|o que|oque)\s*$/;
/** Muleta entre o nome e o pedido ("Atlas, é… manda entrar"). */
const MULETA = '(?:(?:e|eh|ah|ahn|hum|entao)\\s+)*';

// ─── Frases dos pedidos (texto já normalizado, logo depois do nome) ───
const POLIDO = '(?:pode\\s+|por\\s+favor\\s+)?';
/** "abre o próximo", "abre a ficha do próximo", "abra a próxima". */
const ABRE_PROXIMO = 'abr\\w*\\s+(?:a\\s+ficha\\s+d[oa]\\s+)?(?:[oa]\\s+)?proxim[oa]\\b';
/**
 * "manda entrar", "pode mandar entrar", "manda o próximo (paciente) entrar",
 * "manda ele entrar", "chama o próximo", "chama o paciente".
 */
const MANDA_ENTRAR =
  `${POLIDO}(?:(?:mand\\w*|cham\\w*)\\s+(?:[oa]\\s+)?(?:proxim[oa]|paciente)` +
  '|mand\\w*\\s+(?:el[ea]\\s+|(?:[oa]\\s+)?(?:proxim[oa]\\s+)?(?:paciente\\s+)?)entrar)\\b';
/**
 * "abre a agenda", "volta pra agenda", "vai para a agenda". O verbo é
 * obrigatório: "agenda o retorno pra março" é o verbo agendar.
 */
const AGENDA = '(?:abr\\w*|volt\\w*|vai|mostr\\w*)\\s+(?:(?:pra|para)\\s+)?(?:a\\s+)?agenda\\b';
/** "imprime …", "pode imprimir …" — o que imprimir sai de `printTargetsFromSpeech`. */
const IMPRIME = `${POLIDO}imprim\\w*\\b`;
/**
 * "salve", "salva", "pode salvar", "registra no prontuário" (05/10/2026): o
 * mesmo botão "Registrar no prontuário" da coluna do ditado.
 */
const SALVAR = `${POLIDO}(?:salv[ae]r?|registr[ae]r?)\\b`;
/** "pause", "pausa", "pausar o ditado" (05/10/2026): pausa o ao vivo. */
const PAUSAR = '(?:pausa|pause|pausar)\\b';
/**
 * "continua", "retoma", "volta a ouvir" (05/10/2026): tira o ao vivo da
 * pausa. Só a escuta ouve isso (pausado, o ao vivo não transcreve nada).
 */
const RETOMAR = '(?:continua|continue|retoma|retome|volt[ae]\\s+a\\s+(?:ouvir|escutar))\\b';
/**
 * "desliga", "desliga o ditado", "encerra o ditado", "para de ouvir", "parar
 * ao vivo". "Para" e "encerra" só com o objeto: "encerra o atendimento" não
 * é desligar o ditado.
 */
const PARAR =
  '(?:deslig\\w*|(?:para|pare|parar|encerra|encerre)\\s+' +
  '(?:o\\s+ditado|o\\s+ao\\s+vivo|ao\\s+vivo|a\\s+escuta|de\\s+ouvir|de\\s+escutar))\\b';
/**
 * "Pare!" sozinho (a frase acaba ali). Na transcrição do ao vivo a fala
 * continua ("para o paciente…" é preposição): lá só "pare" colado no "Atlas".
 */
const PARAR_SOLTO = /^(?:para|pare|parar)$/;

/** A ordem importa: os específicos antes dos genéricos. */
const PEDIDOS_SISTEMA: Array<[RegExp, SystemCommand]> = [
  [new RegExp(`^${ABRE_PROXIMO}`), 'proximo'],
  [new RegExp(`^${MANDA_ENTRAR}`), 'mandarEntrar'],
  [new RegExp(`^${AGENDA}`), 'agenda'],
  [new RegExp(`^${IMPRIME}`), 'imprimir'],
  [new RegExp(`^${SALVAR}`), 'salvar'],
  [new RegExp(`^${PAUSAR}`), 'pausar'],
  [new RegExp(`^${RETOMAR}`), 'retomar'],
  [new RegExp(`^${PARAR}`), 'parar'],
  [PARAR_SOLTO, 'parar'],
];
const PEDIDOS_DITADO: Array<[RegExp, DitadoCommand]> = [
  [new RegExp(`^${PARAR}`), 'parar'],
  [PARAR_SOLTO, 'parar'],
  [/^ao vivo\b/, 'aoVivo'],
  [/^(abrir|abre|abra)\b/, 'abrir'],
  [/^(cancela|cancelar|cancele)\b/, 'cancela'],
];

/**
 * O que imprimir, pelo que vem depois do "imprime" — mesma leitura da função
 * `imprimir_documentos` do ao vivo: "receita" sozinha é a de medicamentos;
 * sem dizer o quê, é tudo.
 */
export function printTargetsFromSpeech(texto: string): PrintTarget[] {
  const t = normalizeSpeech(texto);
  if (/\b(tudo|todos|todas|documentos)\b/.test(t)) return ['tudo'];
  const alvos: PrintTarget[] = [];
  if (/\b(oculos|grau)\b/.test(t)) alvos.push('receitaOculos');
  const receitaSemOculos = /\breceita\b/.test(t) && !/\breceita\s+(?:d[aeo]s?\s+)?oculos\b/.test(t);
  if (receitaSemOculos || /\b(colirios?|remedios?|medicamentos?)\b/.test(t)) alvos.push('receitaMedicamentos');
  if (/\bexames?\b/.test(t)) alvos.push('solicitacaoExames');
  if (/\b(cirurgias?|procedimentos?|laser)\b/.test(t)) alvos.push('solicitacaoCirurgia');
  if (/\b(atestados?|declarac(?:ao|oes)|laudos?|relatorios?)\b/.test(t)) alvos.push('atestadoLaudo');
  return alvos.length > 0 ? alvos : ['tudo'];
}

/**
 * O pedido ao sistema numa fala SEM o nome: o que vem depois de "Atlas" na
 * mesma frase, ou a frase seguinte quando "Atlas" veio sozinho (o Chrome
 * corta na pausa depois do nome).
 */
export function matchSystemCommand(texto: string): SystemCommandMatch | null {
  const t = normalizeSpeech(texto).replace(new RegExp(`^${MULETA}`), '');
  const comando = PEDIDOS_SISTEMA.find(([re]) => re.test(t))?.[1];
  if (!comando) return null;
  if (comando !== 'imprimir') return { comando };
  return { comando, alvos: printTargetsFromSpeech(t.replace(/^.*?imprim\w*/, '')) };
}

export function matchWake(texto: string): WakeMatch | null {
  const t = normalizeSpeech(texto);
  const nome = NOME.exec(t);
  const m = nome ?? QUASE.exec(t);
  if (!m) return null;
  const antes = t.slice(0, m.index);
  const depois = t.slice(m.index + m[0].length).trim();
  const comOk = OK_ANTES.test(antes);
  if (nome && E_ATLAS.test(nome[1])) {
    const pedido = matchSystemCommand(depois);
    return {
      nome: 'atlas',
      comOk,
      variante: nome[1],
      comando: pedido?.comando ?? null,
      ...(pedido?.alvos ? { alvos: pedido.alvos } : {}),
      quase: false,
    };
  }
  return {
    nome: 'jarvis',
    comOk,
    variante: m[1],
    comando: PEDIDOS_DITADO.find(([re]) => re.test(depois))?.[1] ?? null,
    quase: !nome,
  };
}

/** "Jarvis" que vale: nome aceito (não grafia parecida) e não "…, cancela". */
export function isFullWake(m: WakeMatch | null): boolean {
  return !!m && m.nome === 'jarvis' && !m.quase && m.comando !== 'cancela';
}

/**
 * Comandos que o ao vivo pega na própria transcrição. Imprimir e salvar ficam
 * com o modelo (`imprimir_documentos`, `registrar_no_prontuario`) — atender
 * aqui também faria duas vezes; retomar não tem como (pausado, nada é
 * transcrito). Pausar e parar valem nos dois (repetir não faz mal).
 */
export type LiveVoiceCommand = 'proximo' | 'mandarEntrar' | 'agenda' | 'pausar' | 'parar';
export const LIVE_VOICE_COMMANDS: LiveVoiceCommand[] = ['proximo', 'mandarEntrar', 'agenda', 'pausar', 'parar'];
const COM_ATLAS = `\\b(?:${GRAFIAS_ATLAS})\\s+${MULETA}`;
const NA_TRANSCRICAO: Record<LiveVoiceCommand, RegExp> = {
  proximo: new RegExp(`${COM_ATLAS}${ABRE_PROXIMO}`, 'g'),
  mandarEntrar: new RegExp(`${COM_ATLAS}${MANDA_ENTRAR}`, 'g'),
  agenda: new RegExp(`${COM_ATLAS}${AGENDA}`, 'g'),
  pausar: new RegExp(`${COM_ATLAS}${PAUSAR}`, 'g'),
  // Desligar vale pelos dois nomes: "Jarvis, desliga" é o ditado mandando
  // parar. "Pare" sozinho só colado no "Atlas" (05/10/2026); "para" sozinho
  // não ("Atlas, para o paciente…" é preposição — a fala segue).
  parar: new RegExp(
    `\\b(?:(?:${GRAFIAS}|${GRAFIAS_ATLAS})\\s+${MULETA}${PARAR}|(?:${GRAFIAS_ATLAS})\\s+${MULETA}(?:pare|parar)\\b)`,
    'g',
  ),
};

/**
 * Quantas vezes cada comando aparece no texto. Com o ao vivo ligado a escuta
 * fica pausada e quem ouve é a transcrição do serviço, que chega em pedaços e
 * só cresce: conta de novo a cada pedaço e compara com o que já foi atendido.
 * "Imprime" não entra: com o ao vivo ligado, quem imprime é o modelo
 * (`imprimir_documentos`) — atender aqui também imprimiria duas vezes.
 */
export function countLiveCommands(texto: string): Record<LiveVoiceCommand, number> {
  const t = normalizeSpeech(texto);
  const conta = (re: RegExp) => t.match(re)?.length ?? 0;
  return {
    proximo: conta(NA_TRANSCRICAO.proximo),
    mandarEntrar: conta(NA_TRANSCRICAO.mandarEntrar),
    agenda: conta(NA_TRANSCRICAO.agenda),
    pausar: conta(NA_TRANSCRICAO.pausar),
    parar: conta(NA_TRANSCRICAO.parar),
  };
}

/**
 * Quantos "Atlas" o texto tem. Com o ao vivo ligado, um "Atlas" novo sem
 * pedido colado acende a espera (e a lista de pedidos), como na escuta.
 */
export function countAtlas(texto: string): number {
  return normalizeSpeech(texto).match(new RegExp(`\\b(?:${GRAFIAS_ATLAS})\\b`, 'g'))?.length ?? 0;
}

/** "Cancela" (ou "não chamei") dito na janela antes de conectar. */
export function isCancelSpeech(texto: string): boolean {
  return /\b(cancela|cancelar|cancele|nao chamei)\b/.test(normalizeSpeech(texto));
}
